import React, { useState, useEffect, useRef } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';

const GlobalSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const searchRef = useRef(null);
  const { isDark } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (query.trim().length >= 2) {
        fetchSuggestions();
      } else {
        setSuggestions([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query]);

  const fetchSuggestions = async () => {
    try {
      const response = await api.get(`/portal/search/suggestions?query=${encodeURIComponent(query)}`);
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };

  const handleSearch = async (searchQuery) => {
    if (!searchQuery || searchQuery.trim().length < 2) return;

    setLoading(true);
    try {
      const response = await api.get(`/portal/search/quick?query=${encodeURIComponent(searchQuery)}`);
      setResults(response.data.results || []);
      setIsOpen(true);
    } catch (error) {
      console.error('Error searching:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    if (value.trim().length >= 2) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  const handleResultClick = (result) => {
    setIsOpen(false);
    setQuery('');

    // Navigate based on entity type
    switch (result.entity_type) {
      case 'employee':
        navigate(`/employees?id=${result.entity_id}`);
        break;
      case 'task':
        // Navigate to tasks view
        break;
      case 'announcement':
        // Could open a modal or navigate to announcements
        break;
      case 'document':
        navigate(`/documents?id=${result.entity_id}`);
        break;
      default:
        break;
    }
  };

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setSuggestions([]);
    setIsOpen(false);
  };

  const getEntityIcon = (type) => {
    const icons = {
      employee: '👤',
      task: '✓',
      announcement: '📢',
      document: '📄',
      bookmark: '🔖',
      event: '📅',
      organization: '🏢'
    };
    return icons[type] || '📄';
  };

  const getEntityColor = (type) => {
    const colors = {
      employee: 'bg-blue-100 text-blue-800',
      task: 'bg-green-100 text-green-800',
      announcement: 'bg-yellow-100 text-yellow-800',
      document: 'bg-purple-100 text-purple-800',
      bookmark: 'bg-pink-100 text-pink-800',
      event: 'bg-indigo-100 text-indigo-800',
      organization: 'bg-gray-100 text-gray-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div ref={searchRef} className="relative w-full max-w-2xl">
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSearch(query);
            }
          }}
          placeholder="Search employees, documents, tasks, and more..."
          className="block w-full pl-10 pr-10 py-3 border theme-border-primary rounded-lg theme-bg-primary theme-text-primary focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
        />
        {query && (
          <button
            onClick={clearSearch}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
          </button>
        )}
      </div>

      {/* Search Results Dropdown */}
      {isOpen && (query.trim().length >= 2) && (
        <div className={`absolute z-50 mt-2 w-full theme-bg-primary rounded-lg shadow-xl border theme-border-primary max-h-96 overflow-y-auto ${isDark ? 'dark' : ''}`}>
          {loading ? (
            <div className="px-4 py-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 theme-text-secondary">Searching...</p>
            </div>
          ) : (
            <>
              {/* Suggestions */}
              {suggestions.length > 0 && (
                <div className="border-b theme-border-primary">
                  <div className="px-4 py-2 theme-text-secondary text-xs font-semibold uppercase">
                    Suggestions
                  </div>
                  {suggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setQuery(suggestion.suggestion);
                        handleSearch(suggestion.suggestion);
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <span>{getEntityIcon(suggestion.type)}</span>
                        <span className="theme-text-primary">{suggestion.suggestion}</span>
                        <span className={`ml-auto text-xs px-2 py-1 rounded-full ${getEntityColor(suggestion.type)}`}>
                          {suggestion.type}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Results */}
              {results.length > 0 ? (
                <>
                  <div className="px-4 py-2 theme-text-secondary text-xs font-semibold uppercase">
                    Results
                  </div>
                  {results.map((result, index) => (
                    <button
                      key={index}
                      onClick={() => handleResultClick(result)}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-b theme-border-primary last:border-b-0"
                    >
                      <div className="flex items-start space-x-3">
                        <span className="text-2xl">{getEntityIcon(result.entity_type)}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <h4 className="font-medium theme-text-primary truncate">
                              {result.title}
                            </h4>
                            <span className={`text-xs px-2 py-1 rounded-full ${getEntityColor(result.entity_type)}`}>
                              {result.entity_type}
                            </span>
                          </div>
                          {result.content_preview && (
                            <p className="mt-1 text-sm theme-text-secondary line-clamp-2">
                              {result.content_preview}
                            </p>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </>
              ) : suggestions.length === 0 && !loading && (
                <div className="px-4 py-8 text-center theme-text-secondary">
                  No results found for "{query}"
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
